import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import CopyIconButton from '@/components/ui/copy-to-clipboard/copy-icon-button';
import ConfirmationDialog from '@/components/ui/dialog-wrapper/ConfirmationDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { SyntaxHighlighter } from '@/components/ui/syntax-highlighter';
import { toastSomethingError } from '@/components/ui/toast-content';
import { cn } from '@/lib/utils';
import { CloudUpload } from 'lucide-react';
import DeploymentCallApi from './call-api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { WrapTableAndSheet } from '../../tasks/tasks';
import {
  useGetServerlessEndpointById,
  useListServerlessTasks,
  useStopEndpoint,
  useStartEndpoint,
  useRegenerateApiKey,
  useGetServerlessProjectById,
} from '@/service/serverless-gpu/hook';
import {
  TypeServerless,
  TypeServerlessLabel,
} from '@/service/serverless-gpu/type';
import { Link, useParams } from 'react-router-dom';
import { RouteParams } from '@/router/type';
import { fEndpointServerless } from '@/utils/formatLabel';
import BadgeActiveStatus from '../../components/BadgeActiveStatus';
import { toast } from 'sonner';
import { fFullDateTime } from '@/utils/formatTime';
import { Skeleton } from '@/components/ui/skeleton';
import { compareDateDesc } from '@/utils/date';
import { Badge } from '@/components/ui/badge';
import { ArrowTopRightIcon } from '@radix-ui/react-icons';

const ServerlessDeployment = () => {
  const { projectId } = useParams<RouteParams>();
  const { data: dataProject } = useGetServerlessProjectById({
    project_id: projectId!,
  });
  const { data: dataEndpoint, isLoading: isLoadingEndpoint } =
    useGetServerlessEndpointById(
      {
        project_id: projectId!,
      },
      {
        staleTime: 1000 * 10,
        enabled: !!dataProject?.project?.has_endpoint,
      }
    );

  const { data: dataTasks, isLoading: isLoadingTasks } = useListServerlessTasks(
    {
      project_id: projectId!,
      // trigger_type: TypeServerless.Endpoint,
    }
  );
  const regenerateAPIKey = useRegenerateApiKey({
    onSuccess: () => {
      toast.success('API Key regenerated');
    },
    onError: () => {
      toastSomethingError();
    },
  });
  const startEndpoint = useStartEndpoint({
    onSuccess: () => {
      toast.success('Endpoint started');
    },
    onError: () => {
      toastSomethingError();
    },
  });
  const stopEndpoint = useStopEndpoint({
    onSuccess: () => {
      toast.success('Endpoint stopped');
    },
    onError: () => {
      toastSomethingError();
    },
  });

  const tasks =
    dataTasks?.tasks
      ?.filter((item) =>
        [TypeServerless.Server, TypeServerless.Function].includes(
          item.trigger_type
        )
      )
      .sort(compareDateDesc('created_at')) || [];

  const isDeployed = dataEndpoint?.endpoint;

  const result = {
    endpoint: dataEndpoint?.endpoint
      ? fEndpointServerless(dataEndpoint?.endpoint)
      : {
          all: '',
          function: '',
          server: '',
        },
    apikey: dataEndpoint?.api_key || '',
    isActive: dataEndpoint?.is_active || false,
    deployedAt: dataEndpoint?.deployed_at || '',
  };

  const handleRegenerateAPIKey = () => {
    regenerateAPIKey.mutate({ project_id: projectId! });
  };

  const handleStartEndpoint = () => {
    startEndpoint.mutate({ project_id: projectId! });
  };

  const handleStopEndpoint = () => {
    stopEndpoint.mutate({ project_id: projectId! });
  };

  return (
    <>
      <div className="container mt-4">
        {isLoadingEndpoint ? (
          <Skeleton className="h-[100px]" />
        ) : isDeployed ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <p className="text-base font-semibold">Deploy</p>
              <BadgeActiveStatus status={result?.isActive} />
            </div>
            {result?.isActive ? (
              // <Dialog>
              //   <DialogTrigger asChild>
              //     <Button variant={'destructive'}>Stop</Button>
              //   </DialogTrigger>
              //   <DialogContent className="max-w-[700px]">
              //     <DialogHeader>
              //       <DialogTitle>Stop Endpoint</DialogTitle>
              //     </DialogHeader>
              //     <SyntaxHighlighter language="bash" showCopy wrapLines>
              //       {`float16 endpoint stop`}
              //     </SyntaxHighlighter>
              //   </DialogContent>
              // </Dialog>
              <div className="flex items-center gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button>Example Call API</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-[700px]">
                    <DialogHeader>
                      <DialogTitle>Call Endpoint</DialogTitle>
                    </DialogHeader>
                    <div className="overflow-auto">
                      <DeploymentCallApi
                        apiKey={result.apikey}
                        endpoint={result.endpoint.function}
                      />
                    </div>
                  </DialogContent>
                </Dialog>
                <ConfirmationDialog
                  title="Stop Endpoint"
                  message="Are you sure you want to stop?"
                  onConfirm={handleStopEndpoint}
                  confirmText="Stop"
                >
                  <Button
                    variant={'destructive'}
                    disabled={stopEndpoint.isPending}
                  >
                    Stop
                  </Button>
                </ConfirmationDialog>
              </div>
            ) : (
              // <Dialog>
              //   <DialogTrigger asChild>
              //     <Button variant={'secondary'}>Start</Button>
              //   </DialogTrigger>
              //   <DialogContent className="max-w-[700px]">
              //     <DialogHeader>
              //       <DialogTitle>Start Endpoint</DialogTitle>
              //     </DialogHeader>
              //     <SyntaxHighlighter language="bash" showCopy wrapLines>
              //       {`float16 endpoint start`}
              //     </SyntaxHighlighter>
              //   </DialogContent>
              // </Dialog>
              <ConfirmationDialog
                title="Start Endpoint"
                message="Are you sure you want to start?"
                onConfirm={handleStartEndpoint}
                confirmText="Start"
              >
                <Button
                  variant={'secondary'}
                  disabled={startEndpoint.isPending}
                >
                  Start
                </Button>
              </ConfirmationDialog>
            )}
          </div>
        ) : (
          <></>
        )}
        <div className="mt-3">
          {isLoadingEndpoint ? (
            <></>
          ) : isDeployed ? (
            <div className="grid gap-6">
              <div className={cn('relative flex flex-col gap-4')}>
                <div className="flex flex-wrap gap-4">
                  <div className="grid w-[150px] items-center gap-1.5 text-xs">
                    <Label className="text-xs text-muted-foreground">
                      Deployed at
                    </Label>
                    {fFullDateTime(result?.deployedAt)}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="min-w-28 text-muted-foreground">
                    Endpoint
                  </Label>
                  {/* <div className="mt-1 flex items-center gap-2">
                    <Label className="min-w-28 text-muted-foreground">
                      <Badge variant={'secondary'}>
                        {TypeServerlessLabel[TypeServerless.Server]}
                      </Badge>
                    </Label>
                    <div className="w-full max-w-lg">
                      <div className="flex items-center gap-2">
                        <p className="text-sm">{result?.endpoint.server}</p>
                        <CopyIconButton text={result?.endpoint.server} />
                      </div>
                    </div>
                  </div> */}
                  <div className="flex items-center gap-2">
                    <Label className="min-w-28 text-muted-foreground">
                      <Badge variant={'secondary'}>
                        {TypeServerlessLabel[TypeServerless.Function]}
                      </Badge>
                    </Label>
                    <div className="w-full max-w-lg">
                      <div className="flex items-center gap-2">
                        <p className="text-sm">{result?.endpoint.function}</p>
                        <CopyIconButton text={result?.endpoint.function} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Label className="min-w-28 text-muted-foreground">
                    API Key
                  </Label>
                  <div className="w-full max-w-lg">
                    <Input
                      value={
                        isDeployed
                          ? result?.apikey
                          : 'API Key is not yet available.'
                      }
                      readOnly
                      endIcon={
                        isDeployed && <CopyIconButton text={result?.apikey} />
                      }
                      disabled={!isDeployed}
                    />
                  </div>
                  <ConfirmationDialog
                    title="Regenerate API Key"
                    message="Are you sure you want to re-generate?"
                    onConfirm={handleRegenerateAPIKey}
                    confirmText="Regenerate new key"
                  >
                    <Button
                      variant={'secondary'}
                      // disabled={regenerateAPI.isPending}
                    >
                      Re-generate
                    </Button>
                  </ConfirmationDialog>
                  {/* <Dialog>
                    <DialogTrigger asChild>
                      <Button variant={'secondary'}>Re-generate</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-[700px]">
                      <DialogHeader>
                        <DialogTitle>Regenerate API Key</DialogTitle>
                      </DialogHeader>
                      <SyntaxHighlighter language="bash" showCopy wrapLines>
                        {`float16 endpoint regen-token`}
                      </SyntaxHighlighter>
                    </DialogContent>
                  </Dialog> */}
                </div>
              </div>

              <Separator />
              <div>
                <div className="text-base font-semibold">Tasks</div>
                <WrapTableAndSheet
                  data={tasks?.map((item) => ({
                    id: item.id,
                    status: item.status,
                    name: item.name,
                    created_at: item.created_at,
                    type: item.trigger_type,
                  }))}
                  isLoading={isLoadingTasks}
                />
              </div>
            </div>
          ) : (
            <>
              <Alert variant={'info'}>
                <CloudUpload className="h-4 w-4" />
                <AlertTitle>No deployment yet</AlertTitle>
                <AlertDescription>
                  To deploy the endpoint, enter your shell and run this command
                  from the working directory.
                  <div className="mt-2 max-w-[600px]">
                    <SyntaxHighlighter language="bash" showCopy>
                      {`float16 deploy <YOUR_FILE>`}
                    </SyntaxHighlighter>
                  </div>
                  <Link
                    to="https://github.com/float16-cloud/examples/tree/main/official/deploy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant={'link'}
                      endIcon={<ArrowTopRightIcon />}
                      className="mt-4 px-0"
                    >
                      Go to our examples
                    </Button>
                  </Link>
                </AlertDescription>
              </Alert>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ServerlessDeployment;
